import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { PageHeader } from '@/components/ui/page-header'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/badge'
import { formatDateTime } from '@/lib/utils'
import { useAuth } from '@/hooks/use-auth'
import { toast } from 'sonner'
import { useEffect, useState } from 'react'
import { ShieldCheck, ShieldOff, KeyRound, Mail, Building2, User as UserIcon, Calendar, Copy } from 'lucide-react'
import QRCode from 'qrcode'

interface Me {
  id: string
  email: string
  name: string
  role: string
  mfa_enabled: number
  mfa_enforced: number
  status: string
  last_login_at: string | null
  created_at: string | null
  tenant_id: string
  tenant_name: string
  tenant_slug: string
  plan: string
}

const roleLabels: Record<string, string> = {
  super_admin: 'Administrador Global',
  tenant_admin: 'Administrador del Cliente',
  tenant_viewer: 'Visor',
}

export function ProfilePage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: me, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<Me>('/auth/me'),
  })

  // ── Password change ────────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('')

  const changePasswordMutation = useMutation({
    mutationFn: () => api.put('/auth/me/password', { currentPassword, newPassword }),
    onSuccess: () => {
      toast.success('Contrasena actualizada. Otras sesiones se han cerrado.')
      setCurrentPassword('')
      setNewPassword('')
      setNewPasswordConfirm('')
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const submitPassword = (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword.length < 8) return toast.error('La contrasena debe tener al menos 8 caracteres')
    if (newPassword !== newPasswordConfirm) return toast.error('Las contrasenas no coinciden')
    changePasswordMutation.mutate()
  }

  // ── MFA setup ──────────────────────────────────────────────
  const [setupSecret, setSetupSecret] = useState<{ secret: string; otpauth: string } | null>(null)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [verifyCode, setVerifyCode] = useState('')
  const [disablePassword, setDisablePassword] = useState('')
  const [showDisableForm, setShowDisableForm] = useState(false)

  const setupMutation = useMutation({
    mutationFn: () => api.post<{ secret: string; otpauth: string }>('/auth/me/mfa/setup'),
    onSuccess: (data) => setSetupSecret(data),
    onError: (err) => toast.error((err as Error).message),
  })

  useEffect(() => {
    if (!setupSecret) {
      setQrDataUrl(null)
      return
    }
    QRCode.toDataURL(setupSecret.otpauth, { width: 220, margin: 1 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null))
  }, [setupSecret])

  const verifyMutation = useMutation({
    mutationFn: () => api.post('/auth/me/mfa/verify', { code: verifyCode.replace(/\s+/g, '') }),
    onSuccess: () => {
      toast.success('MFA activado correctamente')
      setSetupSecret(null)
      setVerifyCode('')
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const disableMutation = useMutation({
    mutationFn: () => api.delete('/auth/me/mfa', { password: disablePassword }),
    onSuccess: () => {
      toast.success('MFA desactivado')
      setDisablePassword('')
      setShowDisableForm(false)
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
    onError: (err) => toast.error((err as Error).message),
  })

  if (isLoading || !me) {
    return (
      <div className="space-y-6">
        <PageHeader title="Mi perfil" />
        <div className="h-32 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-100)' }} />
      </div>
    )
  }

  const mfaEnabled = !!me.mfa_enabled
  const mfaEnforced = !!me.mfa_enforced

  return (
    <div className="space-y-6">
      <PageHeader title="Mi perfil" description="Informacion de tu cuenta y opciones de seguridad" />

      {/* Account info */}
      <Card>
        <CardHeader><CardTitle>Informacion personal</CardTitle></CardHeader>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <InfoRow icon={UserIcon} label="Nombre" value={me.name} />
          <InfoRow icon={Mail} label="Email" value={me.email} />
          <InfoRow icon={Building2} label="Cliente" value={me.tenant_name} />
          <InfoRow icon={KeyRound} label="Rol" value={roleLabels[me.role] ?? me.role} />
          <InfoRow icon={Calendar} label="Ultimo acceso" value={me.last_login_at ? formatDateTime(me.last_login_at) : 'Sin registros'} />
          <InfoRow icon={Calendar} label="Cuenta creada" value={me.created_at ? formatDateTime(me.created_at) : '—'} />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-4" style={{ borderColor: 'var(--color-border)' }}>
          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Estado:</span>
          <StatusBadge status={me.status} />
          <span className="ml-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>MFA:</span>
          {mfaEnabled ? (
            <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold"
              style={{ backgroundColor: 'var(--color-success-light)', color: 'var(--color-success-dark)' }}>
              <ShieldCheck className="h-3.5 w-3.5" /> Activo
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold"
              style={{ backgroundColor: 'var(--color-warning-light)', color: 'var(--color-warning)' }}>
              <ShieldOff className="h-3.5 w-3.5" /> Inactivo
            </span>
          )}
        </div>
      </Card>

      {/* Change password */}
      <Card>
        <CardHeader><CardTitle>Cambiar contrasena</CardTitle></CardHeader>
        <form onSubmit={submitPassword} className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
              Contrasena actual
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={{ borderColor: 'var(--color-border)' }}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
              Nueva contrasena (min 8)
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={{ borderColor: 'var(--color-border)' }}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
              Confirmar nueva contrasena
            </label>
            <input
              type="password"
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={{ borderColor: 'var(--color-border)' }}
            />
          </div>
          <div className="md:col-span-2">
            <Button type="submit" loading={changePasswordMutation.isPending}>
              Actualizar contrasena
            </Button>
            <p className="mt-2 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
              Al cambiar la contrasena se cerraran todas las demas sesiones activas.
            </p>
          </div>
        </form>
      </Card>

      {/* MFA */}
      <Card>
        <CardHeader>
          <CardTitle>Autenticacion en dos pasos (TOTP)</CardTitle>
        </CardHeader>
        <p className="mb-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Anade una capa adicional de seguridad usando una app TOTP como Microsoft Authenticator, Google Authenticator o 1Password.
        </p>

        {mfaEnabled ? (
          <div className="space-y-3">
            <div className="rounded-md border p-3 text-sm"
              style={{ borderColor: 'var(--color-success)', backgroundColor: 'var(--color-success-light)', color: 'var(--color-success-dark)' }}>
              MFA esta activo en tu cuenta. Necesitaras tu app autenticadora cada vez que inicies sesion.
            </div>
            {mfaEnforced ? (
              <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                MFA es obligatorio por politica de la plataforma. Si necesitas desactivarlo, contacta con un administrador.
              </p>
            ) : !showDisableForm ? (
              <Button variant="secondary" onClick={() => setShowDisableForm(true)}>
                Desactivar MFA
              </Button>
            ) : (
              <div className="space-y-2 rounded-md border p-4" style={{ borderColor: 'var(--color-border)' }}>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  Confirma tu contrasena para desactivar MFA.
                </p>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder="Contrasena"
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ borderColor: 'var(--color-border)' }}
                />
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    onClick={() => disableMutation.mutate()}
                    loading={disableMutation.isPending}
                    disabled={!disablePassword}
                  >
                    Confirmar y desactivar
                  </Button>
                  <Button variant="ghost" onClick={() => { setShowDisableForm(false); setDisablePassword('') }}>
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : !setupSecret ? (
          <Button variant="primary" onClick={() => setupMutation.mutate()} loading={setupMutation.isPending}>
            <ShieldCheck className="h-4 w-4" /> Activar MFA
          </Button>
        ) : (
          <div className="space-y-4">
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Escanea el codigo QR con tu app TOTP. Si no puedes escanearlo, introduce la clave manualmente.
            </p>
            <div className="flex flex-col items-start gap-4 md:flex-row">
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="QR MFA" width={220} height={220} className="rounded-md border"
                  style={{ borderColor: 'var(--color-border)' }} />
              ) : (
                <div className="h-[220px] w-[220px] animate-pulse rounded-md"
                  style={{ backgroundColor: 'var(--color-gray-100)' }} />
              )}
              <div className="flex-1 space-y-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>
                    Clave manual
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="rounded-md border px-2 py-1 text-xs font-mono"
                      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-gray-50)' }}>
                      {setupSecret.secret.match(/.{1,4}/g)?.join(' ')}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(setupSecret.secret)
                        toast.success('Clave copiada')
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" /> Copiar
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                    Codigo de verificacion (6 digitos)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="w-32 rounded-md border px-3 py-2 text-center text-lg tabular-nums tracking-widest"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    onClick={() => verifyMutation.mutate()}
                    loading={verifyMutation.isPending}
                    disabled={verifyCode.length !== 6}
                  >
                    Verificar y activar
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { setSetupSecret(null); setVerifyCode('') }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Hidden tenant placeholder so user is referenced even if me is fully covered */}
      {!user && null}
    </div>
  )
}

function InfoRow({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
        style={{ backgroundColor: 'var(--color-gray-50)' }}>
        <Icon className="h-4 w-4" style={{ color: 'var(--color-text-secondary)' }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{label}</p>
        <p className="truncate text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{value}</p>
      </div>
    </div>
  )
}
